import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useWorkoutPlanRoutines = (planId?: string) => {
  return useQuery({
    queryKey: ["workout-plan-routines", planId],
    queryFn: async () => {
      if (!planId) return [];

      console.log("🔍 Fetching routines for workout plan:", planId);
      
      try {
        // First get the routine IDs + position from workout_plan_routines
        const { data: routineLinks, error: linksError } = await supabase
          .from('workout_plan_routines')
          .select('routine_id, position')
          .eq('workout_plan_id', planId)
          .order('position', { ascending: true });

        if (linksError) {
          console.error("❌ Error fetching routine links:", linksError);
          throw linksError;
        }

        if (!routineLinks?.length) {
          console.log("📭 No routines found for plan");
          return [];
        }

        const routineIds = routineLinks.map(link => link.routine_id);
        const positionMap = new Map(routineLinks.map(link => [link.routine_id, link.position ?? 0]));
        console.log("🔗 Found routine IDs:", routineIds);

        // Then get the full routine data including exercises and days
        // Use timeout to catch slow RLS policies
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Query timeout - check RLS policies")), 8000)
        );
        
        const queryPromise = supabase
          .from('routines')
          .select(`
            id,
            name,
            routine_days (
              day
            ),
            exercises (
              id,
              name,
              sets,
              reps,
              weight,
              set_data,
              video_url,
              exercise_rest_time,
              set_rest_time,
              position
            )
          `)
          .in('id', routineIds)
          .order('position', { referencedTable: 'exercises', ascending: true });

        const result = await Promise.race([queryPromise, timeoutPromise]) as any;
        const { data: routines, error: routinesError } = result;

        if (routinesError) {
          console.error("❌ Error fetching routines:", routinesError);
          throw routinesError;
        }

        console.log("✅ Successfully fetched routines with exercises:", routines);
        
        // Transform the data with improved day handling to prevent temporary incorrect states
        const transformedRoutines = routines.map(routine => {
          const formattedExercises = routine.exercises?.map(exercise => ({
            ...exercise,
            exercise_rest_time: exercise.exercise_rest_time !== null ? Number(exercise.exercise_rest_time) : null,
            set_rest_time: exercise.set_rest_time !== null ? Number(exercise.set_rest_time) : null,
            set_data: exercise.set_data || []
          })) || [];
          
          // Improved day handling - ensure we have clean, complete day data
          const routineDays = routine.routine_days || [];
          console.log(`📅 Processing days for routine ${routine.name}:`, routineDays);
          
          return {
            ...routine,
            // Store all days, not just the first one to prevent incorrect initialization
            routine_days: routineDays,
            // Keep the single day for backward compatibility but make it more reliable
            day: routineDays.length > 0 ? routineDays[0]?.day : null,
            plan_position: positionMap.get(routine.id) ?? 0,
            exercises: formattedExercises
          };
        }).sort((a, b) => (a.plan_position ?? 0) - (b.plan_position ?? 0));

        console.log("🔄 Transformed routines with improved day handling:", transformedRoutines);
        return transformedRoutines;
      } catch (error) {
        console.error("💥 Error in getPlanRoutines:", error);
        throw error;
      }
    },
    enabled: !!planId,
    // Reduce stale time for faster updates after routine creation
    staleTime: 30000, // 30 seconds - was 5 minutes
    // Keep cache for reasonable time to prevent unnecessary refetches
    gcTime: 300000, // 5 minutes - was 10 minutes
    // Reduce retries to avoid multiple timeouts
    retry: 1,
    retryDelay: 500,
  });
};
